export const QUESTION_STATUS_LABELS = {
  winning: 'Winning',
  slipping: 'Slipping',
  missing: 'Missing',
  holding: 'You\'re in',
  unknown: 'No data',
}

export const QUESTION_FILTERS = [
  { value: 'everything', label: 'Everything' },
  { value: 'missing', label: 'Missing you' },
  { value: 'slipping', label: 'Dropping' },
  { value: 'holding', label: 'Low rank' },
  { value: 'winning', label: 'Winning' },
]

const STATUS_DISPLAY = {
  winning: {
    label: QUESTION_STATUS_LABELS.winning,
    tone: 'success',
  },
  slipping: {
    label: QUESTION_STATUS_LABELS.slipping,
    tone: 'warning',
  },
  missing: {
    label: QUESTION_STATUS_LABELS.missing,
    tone: 'danger',
  },
  holding: {
    label: QUESTION_STATUS_LABELS.holding,
    tone: 'info',
  },
  unknown: {
    label: QUESTION_STATUS_LABELS.unknown,
    tone: 'neutral',
  },
}

const OPPORTUNITY_ORDER = {
  missing: 5,
  slipping: 4,
  holding: 3,
  winning: 2,
  unknown: 1,
}

export function createEmptyQuestionsData () {
  return {
    questions: [],
  }
}

export function getQuestionStatus (question) {
  const rank = question?.yourRank || {}
  const current = rank.current
  const change = Number(rank.change || 0)

  if (current === null || current === undefined) {
    return 'unknown'
  }

  if (Number(current) === 0) {
    return 'missing'
  }

  if (Number(current) > 0 && Number(current) <= 3) {
    return 'winning'
  }

  if (change > 0) {
    return 'slipping'
  }

  return 'holding'
}

export function formatQuestionRank (current) {
  if (current === null || current === undefined || Number(current) === 0) {
    return '-'
  }

  return `#${Number(current).toFixed(1)}`
}

export function formatQuestionIntent (intent) {
  const value = String(intent || '').trim()
  if (!value) {
    return ''
  }

  return `${value.charAt(0).toUpperCase()}${value.slice(1)}`
}

export function formatRankChange (change) {
  if (change === null) {
    return null
  }

  const value = Number(change || 0)
  const absoluteValue = Math.abs(value)

  if (!value) {
    return {
      direction: 'flat',
      tone: 'neutral',
      label: 'No change',
      value,
    }
  }

  if (value < 0) {
    return {
      direction: 'up',
      tone: 'positive',
      label: `+${formatDecimal(absoluteValue)}`,
      value,
    }
  }

  return {
    direction: 'down',
    tone: 'negative',
    label: `-${formatDecimal(absoluteValue)}`,
    value,
  }
}

export function normalizeQuestionRows (questions) {
  return (questions || []).map((question) => {
    const status = getQuestionStatus(question)
    const statusDisplay = STATUS_DISPLAY[status]
    const yourRank = question.yourRank || {}
    const currentRank = yourRank.current
    const mentionedBrandCount = Number(question.mentionedBrandCount || 0)
    const topBrands = normalizeCountRows(question.topBrands, 'mentionedAnswerCount')
    const topCitations = normalizeCountRows(question.topCitations, 'citationCount')
    const normalizedQuestion = {
      ...question,
      askedCount: Number(question.askedCount || 0),
      mentionedBrandCount,
      topBrands,
      topCitations,
      intentLabel: formatQuestionIntent(question.intent),
      status,
      statusLabel: statusDisplay.label,
      statusTone: statusDisplay.tone,
      rankLabel: formatQuestionRank(currentRank),
      rankSupportLabel: buildRankSupportLabel(question),
      rankDenominatorLabel: buildRankDenominatorLabel(question),
      trend: formatRankChange(yourRank.change),
      tableMetaLabel: buildTableMetaLabel(question),
    }

    return {
      ...normalizedQuestion,
      searchableText: buildSearchableText(normalizedQuestion),
    }
  })
}

export function buildQuestionSummary (rows) {
  const counts = buildQuestionStatusCounts(rows)
  const missingRows = rows.filter(row => row.status === 'missing')

  return {
    total: rows.length,
    winning: counts.winning,
    slipping: counts.slipping,
    missing: counts.missing,
    holding: counts.holding,
    unknown: counts.unknown,
    biggestOpening: {
      status: 'missing',
      count: missingRows.length,
      label: `${missingRows.length} missing`,
      detail: "Competitors rank, you don't",
    },
  }
}

export function buildQuestionFilterCounts (rows) {
  const counts = buildQuestionStatusCounts(rows)

  return {
    everything: rows.length,
    missing: counts.missing,
    slipping: counts.slipping,
    holding: counts.holding,
    winning: counts.winning,
  }
}

export function questionMatchesSearch (row, search) {
  const query = String(search || '').trim().toLowerCase()
  if (!query) {
    return true
  }

  return String(row.searchableText || '').toLowerCase().indexOf(query) > -1
}

export function sortQuestionsByOpportunity (rows) {
  return rows.slice().sort((first, second) => {
    const statusDifference = (OPPORTUNITY_ORDER[second.status] || 0) - (OPPORTUNITY_ORDER[first.status] || 0)
    if (statusDifference) {
      return statusDifference
    }

    return Number(second.askedCount || 0) - Number(first.askedCount || 0)
  })
}

function buildQuestionStatusCounts (rows) {
  return rows.reduce((counts, row) => {
    if (counts[row.status] === undefined) {
      return counts
    }

    counts[row.status] += 1
    return counts
  }, {
    winning: 0,
    slipping: 0,
    missing: 0,
    holding: 0,
    unknown: 0,
  })
}

function buildRankSupportLabel (question) {
  const status = getQuestionStatus(question)

  if (status === 'missing' && question.dominantBrand) {
    return `${question.dominantBrand} dominates here`
  }

  if (status === 'winning') {
    return 'top 3 consistently'
  }

  if (status === 'holding') {
    return 'stable, mid-pack'
  }

  if (status === 'slipping') {
    return `dropped ${formatDecimal(question.yourRank.change)} spots this week`
  }

  const brandCount = Number(question.mentionedBrandCount || 0)
  return `of ${brandCount} brands`
}

function buildRankDenominatorLabel (question) {
  const status = getQuestionStatus(question)
  if (status === 'unknown') {
    return 'No rank information'
  }

  if (status === 'missing') {
    return 'not ranked'
  }

  const brandCount = Number(question.mentionedBrandCount || 0)
  if (!brandCount) {
    return ''
  }

  return `of ${brandCount} brands`
}

function buildTableMetaLabel (question) {
  const brandCount = Number(question.mentionedBrandCount || 0)

  if (!brandCount) {
    return ''
  }

  return `${brandCount} brands in answer`
}

function buildSearchableText (question) {
  return [
    question.questionText,
    question.intent,
  ]
    .filter(Boolean)
    .join(' ')
}

function normalizeCountRows (rows, countKey) {
  return (rows || []).map(row => ({
    ...row,
    [countKey]: Number(row[countKey] || 0),
  }))
}

function formatDecimal (value) {
  const numericValue = Number(value || 0)
  if (Number.isInteger(numericValue)) {
    return String(numericValue)
  }

  return numericValue.toFixed(1)
}
